import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Search, Phone, Mail, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Leads error:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.put(`${API}/admin/leads/${leadId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Lead status updated");
      fetchLeads();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update status");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.phone.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-leads">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Leads</h1>
            <p className="text-slate-400">Manage incoming lead submissions</p>
          </div>
          <div className="text-sm text-slate-400">
            {filteredLeads.length} of {leads.length} leads
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leads Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-700/50">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-4">Contact</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-4">Debt Info</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-4">Eligibility</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-4">Status</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-4">Date</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-700/30">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{lead.full_name}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-slate-400 text-sm hover:text-[#10B981]">
                            <Mail className="w-3 h-3" />
                            {lead.email}
                          </a>
                        </div>
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-slate-400 text-sm hover:text-[#10B981] mt-1">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-bold">{formatCurrency(lead.debt_amount)}</p>
                        <p className="text-slate-400 text-sm">{lead.number_of_creditors} creditors</p>
                        <p className="text-slate-500 text-xs mt-1">{lead.employment_status.replace(/_/g, ' ')}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.iva_eligible 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {lead.iva_eligible ? 'Eligible' : 'Review Needed'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Select 
                        value={lead.status} 
                        onValueChange={(value) => updateLeadStatus(lead.id, value)}
                      >
                        <SelectTrigger className={`w-32 h-8 text-xs ${
                          lead.status === 'new' ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' :
                          lead.status === 'contacted' ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' :
                          lead.status === 'converted' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' :
                          'bg-slate-500/20 border-slate-500/50 text-slate-400'
                        }`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <a 
                          href={`mailto:${lead.email}`}
                          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                        <a 
                          href={`tel:${lead.phone}`}
                          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredLeads.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                      No leads found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default AdminLeadsPage;
